import React, {useState, useEffect} from 'react';
import {Icon, Text, Button, Radio} from '@/ui-kits';
import {cn, formatDate} from '@/utils/common_functions';
import {LicenceVerificationMode} from '@/constants';
import {useDispatch, useSelector} from 'react-redux';
import {toggleLicenceVerificationRequest, resetUserMessage} from '@/services/redux/slices';
import {
  userStatus,
  userSuccessSelector,
  userFailureSelector,
  licenceVerificationModeSelector,
  licenceVerificationLastUpdatedSelector,
} from '@/services/redux/selectors';
import {useToast} from '@/hooks';
import {getSuccessMessage, getErrorMessage, SuccessCodes, ErrorCodes} from '@/utils/getMessages';
import {resetMessage} from '@/utils/common_functions';

export const SuperAdminSettings: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ==========================
  // selectors
  // ==========================
  const isLoading = useSelector(userStatus);
  const success = useSelector(userSuccessSelector) as SuccessCodes;
  const failure = useSelector(userFailureSelector) as ErrorCodes;
  const licenseMode = useSelector(licenceVerificationModeSelector) as LicenceVerificationMode;
  const lastUpdatedString = useSelector(licenceVerificationLastUpdatedSelector);

  // ==========================
  // states
  // ==========================
  const [localLicenseMode, setLocalLicenseMode] = useState<LicenceVerificationMode>(licenseMode);
  const [originalLicenseMode, setOriginalLicenseMode] = useState<LicenceVerificationMode>(licenseMode);
  const [isEditing, setIsEditing] = useState(false);

  // ==========================
  // handlers
  // ==========================
  const formatLastUpdated = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString, 'dd MMM yyyy, HH:mm:ss');
  };

  const handleEdit = () => {
    // Store current mode as original when starting to edit
    setOriginalLicenseMode(localLicenseMode);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original values
    setLocalLicenseMode(originalLicenseMode);
  };

  const handleSave = () => {
    dispatch(toggleLicenceVerificationRequest({mode: localLicenseMode}));
  };

  // ==========================
  // side effects
  // ==========================
  // Sync local state with Redux state when it changes
  useEffect(() => {
    setLocalLicenseMode(licenseMode);
    setOriginalLicenseMode(licenseMode);
  }, [licenseMode]);

  // Handle success and failure messages
  useEffect(() => {
    if (success) {
      // Check if it's the toggle licence verification success code
      if (String(success) === 'S-10115') {
        setIsEditing(false);
        showToast(getSuccessMessage(success as SuccessCodes), 'success');
      }
      // Reset message after handling
      resetMessage(() => {
        dispatch(resetUserMessage());
      });
    }
    if (failure) {
      showToast(getErrorMessage(failure), 'failure');
      // Reset message after handling
      resetMessage(() => {
        dispatch(resetUserMessage());
      });
    }
  }, [success, failure, dispatch, showToast]);

  return (
    <div className="flex flex-col grow p-3 sm:p-4 md:p-6 lg:p-8 bg-[#F9FAFB] min-h-screen">
     
      <div className="flex flex-col gap-4 sm:gap-6 mt-0 sm:mt-2 md:mt-4 max-w-6xl mx-auto w-full">
        {/* System Configuration Card */}
        <div className="bg-white border border-ui_input_border rounded-2xl p-4 sm:p-4 md:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2 sm:gap-0">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-[#CBEBFB66] rounded-lg flex-shrink-0">
                <Icon type="system" className="size-7 sm:size-7 md:size-8" />
              </div>
              <div>
                <Text variant="heading" className="text-deep_blue !font-FigtreeSemiBold">
                  System Configuration
                </Text>
                <Text variant="body" className="text-deep_blue !font-FigtreeRegular text-sm">
                  [ Doctor Verification Settings ]
                </Text>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-6">
            <div className="flex flex-row items-center justify-between mb-4 gap-2">
              <Text variant="title" className="text-deep_blue !font-FigtreeSemiBold">
                License Verification Mode
              </Text>
              {!isEditing && (
                <button
                  onClick={handleEdit}
                  className="p-2 hover:bg-ui_sky_blue rounded-lg transition-colors flex-shrink-0"
                  aria-label="Edit settings">
                  <Icon type="edit_input" className="size-5 text-ui_blue" />
                </button>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {/* API-based verification option (Automatic) */}
              <button
                type="button"
                onClick={() => {
                  if (isEditing) {
                    setLocalLicenseMode(LicenceVerificationMode.Automatic);
                  }
                }}
                disabled={!isEditing || isLoading}
                className={cn(
                  'flex-1 p-4 rounded-xl border-2 transition-all text-left',
                  localLicenseMode === LicenceVerificationMode.Automatic
                    ? 'border-ui_blue bg-ui_bg_pale_blue'
                    : 'border-ui_input_border bg-white',
                  !isEditing && 'cursor-default',
                  isEditing && !isLoading && 'hover:border-ui_blue/50 cursor-pointer',
                  isLoading && 'opacity-50 cursor-not-allowed',
                )}>
                <div className="flex items-start gap-3">
                  {isEditing && (
                    <div className="mt-1 flex-shrink-0">
                      <Radio
                        value="automatic"
                        checked={localLicenseMode === LicenceVerificationMode.Automatic}
                        onChange={() => {
                          if (isEditing && !isLoading) {
                            setLocalLicenseMode(LicenceVerificationMode.Automatic);
                          }
                        }}
                        disabled={!isEditing || isLoading}
                        label=""
                        className="pointer-events-none"
                        inputClassName="size-5 scale-110"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Text
                      variant="subtitle"
                      className={cn(
                        'font-FigtreeSemiBold mb-3 text-base',
                        localLicenseMode === LicenceVerificationMode.Automatic ? 'text-ui_blue' : 'text-ui_placeholder',
                      )}>
                      API-based verification (Recommended)
                    </Text>
                    <Text variant="body" className={cn('!font-FigtreeRegular', '!text-base', 'mt-4', 'mb-6', localLicenseMode === LicenceVerificationMode.Automatic ? '!text-deep_blue' : 'text-ui_placeholder')}>
                      Doctors must upload license. License is verified automatically via the API.
                    </Text>
                  </div>
                </div>
              </button>

              {/* Manual verification option */}
              <button
                type="button"
                onClick={() => {
                  if (isEditing) {
                    setLocalLicenseMode(LicenceVerificationMode.Manual);
                  }
                }}
                disabled={!isEditing || isLoading}
                className={cn(
                  'flex-1 p-4 rounded-xl border-2 transition-all text-left',
                  localLicenseMode === LicenceVerificationMode.Manual
                    ? 'border-ui_blue bg-ui_bg_pale_blue'
                    : 'border-ui_input_border bg-white',
                  !isEditing && 'cursor-default',
                  isEditing && !isLoading && 'hover:border-ui_blue/50 cursor-pointer',
                  isLoading && 'opacity-50 cursor-not-allowed',
                )}>
                <div className="flex items-start gap-3">
                  {isEditing && (
                    <div className="mt-1 flex-shrink-0">
                      <Radio
                        value="manual"
                        checked={localLicenseMode === LicenceVerificationMode.Manual}
                        onChange={() => {
                          if (isEditing && !isLoading) {
                            setLocalLicenseMode(LicenceVerificationMode.Manual);
                          }
                        }}
                        disabled={!isEditing || isLoading}
                        label=""
                        className="pointer-events-none"
                        inputClassName="size-5 scale-110"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Text
                      variant="subtitle"
                      className={cn(
                        'font-FigtreeSemiBold mb-3 text-base',
                        localLicenseMode === LicenceVerificationMode.Manual ? 'text-ui_blue' : 'text-ui_placeholder',
                      )}>
                      Manual verification
                    </Text>
                    <Text variant="body" className={cn('!font-FigtreeRegular', '!text-base', 'mt-4', 'mb-6', localLicenseMode === LicenceVerificationMode.Manual ? '!text-deep_blue' : 'text-ui_placeholder')}>
                      Doctors enter license number and issuance details. No API verification is performed.
                    </Text>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div className="mt-4 pt-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-baseline gap-2">
                <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-base">
                  Status:
                </Text>
                <Text variant="body" className="text-deep_blue !font-FigtreeBold !text-base">
                  ACTIVE
                </Text>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-2">
                  <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-base">
                    Scope:
                  </Text>
                  <Text variant="body" className="text-deep_blue !font-FigtreeBold !text-base">
                    Global <span className="text-deep_blue !font-FigtreeRegular">(applies to all doctor sign-ups)</span>
                  </Text>
                </div>
                <div className="flex items-baseline gap-2 ml-auto">
                  <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-sm">
                    Last updated on:
                  </Text>
                  <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-sm">
                    {formatLastUpdated(lastUpdatedString)}
                  </Text>
                </div>
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="flex flex-row justify-center gap-4 mt-8">
              <Button
                title="Cancel"
                variant="secondary"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex-1 max-w-[200px]"
              />
              <Button
                title={isLoading ? 'Saving...' : 'Save'}
                variant="primary"
                onClick={handleSave}
                disabled={isLoading}
                className="flex-1 max-w-[200px]"
              />
            </div>
          )}
        </div>

        {/* Compliance & Security Card */}
        <div className="bg-white border border-ui_input_border rounded-2xl p-4 sm:p-4 md:p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-[#CBEBFB66] rounded-lg flex-shrink-0">
              <Icon type="compliance" className="size-6" />
            </div>
            <Text variant="heading" className="text-deep_blue !font-FigtreeSemiBold">
              Compliance & Security
            </Text>
          </div>

          <div>
            <div className="flex flex-row items-center justify-between gap-3 mb-3">
              <Text variant="title" className="text-deep_blue !font-FigtreeSemiBold flex-1 min-w-0">
                Audit Log Retention Period
              </Text>
              <div className="flex items-center gap-3 p-2 px-4 rounded-lg border border-ui_input_border flex-shrink-0">
                <Icon type="dark_lock" className="size-5 text-ui_blue" />
                <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold text-base whitespace-nowrap">
                  6 Years
                </Text>
              </div> 
            </div>
            <div className="flex items-start gap-2 mt-4 p-3 bg-ui_bg_pale_blue rounded-lg border border-ui_input_border">
              <Icon type="i_icon" className="size-5 text-ui_blue mt-0.5 flex-shrink-0" />
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular text-sm">
                Audit logs are retained for a minimum of 6 years to meet healthcare regulatory requirements. This setting
                cannot be changed.
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

